import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"

export type ActivityLog = {
  id: string
  created_at: string
  actor_user_id: string | null
  actor_email: string | null
  actor_name: string | null
  actor_role: string | null
  entity: string
  entity_id: string | null
  action: "create" | "update" | "delete"
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  success: boolean
  error: string | null
}

export type LogsFilters = {
  email?: string
  entity?: string
  entityId?: string
  action?: "create" | "update" | "delete" | "any"
  success?: boolean | "any"
  from?: string | null // ISO string
  to?: string | null // ISO string
  page?: number // 1-based
  limit?: number // page size
}

export type LogsResult = {
  logs: ActivityLog[]
  total: number
  page: number
  limit: number
}

export function usePeopleActivityLogs(filters: LogsFilters) {
  return useQuery<LogsResult>({
    queryKey: ["activityLogs", filters],
    queryFn: async () => {
      const page = filters.page ?? 1
      const limit = filters.limit ?? 25
      const fromIdx = (page - 1) * limit
      const toIdx = fromIdx + limit - 1

      let query = supabase
        .from("activity_logs")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: false })
        .range(fromIdx, toIdx)

      if (filters.email && filters.email.trim()) {
        query = query.ilike("actor_email", `%${filters.email.trim()}%`)
      }

      if (filters.entity && filters.entity.trim()) {
        query = query.ilike("entity", `%${filters.entity.trim()}%`)
      }

      if (filters.entityId && filters.entityId.trim()) {
        query = query.ilike("entity_id", `%${filters.entityId.trim()}%`)
      }

      if (filters.action && filters.action !== "any") {
        query = query.eq("action", filters.action)
      }

      if (typeof filters.success === "boolean") {
        query = query.eq("success", filters.success)
      }

      if (filters.from) {
        query = query.gte("created_at", filters.from)
      }
      if (filters.to) {
        query = query.lte("created_at", filters.to)
      }

      const { data, error, count } = await query
      if (error) throw error

      return {
        logs: (data as ActivityLog[]) ?? [],
        total: count ?? 0,
        page,
        limit,
      }
    },
  })
}
